import axiosInstance, { axiosInstanceCached } from '@/utils/axios';
import { handleApiError, deleteDataCache } from '@/utils/helpers';

// @ Sample cache test logging
// console.log('activeUsers response:', response);
// console.log('activeUsers cached:', response.cached);
// console.log('activeUsers ttl:', response.config?.cache?.ttl);
// console.log('activeUsers config:', response.config);
// console.log('activeUsers duration:', response.duration);
// console.log('============================');

//* Mock user licenses
// import userLicensesData from './mock/admin/userLicensesMock';

//* GENERAL ADMIN METRICS ENDPOINT ======================================
// @ pass stat in payload to get specific stat query
export const adminMetrics = async (payload = {}) => {
  const { fromDate, toDate, stat } = payload;
  let response = {};
  const cacheID = `${fromDate}_${toDate}`;
  // console.log(`admin_metrics-${stat}-${cacheID}`);

  try {
    response = await axiosInstanceCached.post(
      `admin/stats_agg`,
      { from_date: fromDate, to_date: toDate, stat },
      {
        id: `admin_metrics-${stat}-${cacheID}`,
        cache: {
          ttl: 1000 * 60 * 15, // 15 minutes
          // ttl: 1000 * 60 * 60 * 4, // 4 hours
        },
      }
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get number of active users last 120 and total users
export const activeUsers = async (payload = {}) => {
  const { fromDate, toDate } = payload;
  let response = {};

  const url = `/admin/metric/users?fromDate=${fromDate || ''}&toDate=${toDate || ''}`;
  const cacheID = `${fromDate}_${toDate}`;

  try {
    response = await axiosInstanceCached.get(url, {
      id: `active_users-${cacheID}`,
      cache: {
        ttl: 1000 * 60 * 30, // 30 minutes
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get total shots and rounds
// @ stat: 'shots_and_matches'
export const shotsAndRounds = async (payload = {}) => {
  let response = {};

  try {
    //* Call adminMetrics general call with stat type in payload
    response = await adminMetrics(payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get most popular courses last 120 days
// @ stat: 'courses'
export const popularCourses = async (payload = {}) => {
  let response = {};

  try {
    //* Call adminMetrics general call with stat type in payload
    response = await adminMetrics(payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get the number of rounds created per license last 120 days
// @ stat: 'licenses'
export const roundsPerLicense = async (payload = {}) => {
  let response = {};

  try {
    //* Call adminMetrics general call with stat type in payload
    response = await adminMetrics(payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get total shots by club type
// @ stat: 'club_types'
export const clubTypeShots = async (payload = {}) => {
  let response = {};

  try {
    //* Call adminMetrics general call with stat type in payload
    response = await adminMetrics(payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get total shots by tracking system
// @ stat: 'tracking_systems'
export const trackingSystemShots = async (payload = {}) => {
  let response = {};

  try {
    //* Call adminMetrics general call with stat type in payload
    response = await adminMetrics(payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get the game mode metrics
// @ stat: 'game_modes'
export const gameModeMetrics = async (payload = {}) => {
  let response = {};

  try {
    //* Call adminMetrics general call with stat type in payload
    response = await adminMetrics(payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get total shots by practice area = Now using courses endpoint for practice shot data
// @ stat: 'courses'
// @ DEPRECATED
export const practiceAreaShots = async (payload = {}) => {
  const { fromDate, toDate } = payload;
  let response = {};
  const url = `/admin/metric/shots_practicearea?fromDate=${fromDate || ''}&toDate=${toDate || ''}`;
  const cacheID = `${fromDate}_${toDate}`;

  try {
    response = await axiosInstanceCached.get(url, {
      id: `practiceAreaShots-${cacheID}`,
      cache: {
        ttl: 1000 * 60 * 60 * 4, // 4 hours
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get all of the courses with the number of par 3 1st shots
// @ Not used currently
export const par3FirstShots = async () => {
  let response = {};

  try {
    response = await axiosInstance.get(`/admin/metric/courses/par3_1stshot_count`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get all of the license information
//* used for admin license query
export const licenses = async (payload = {}) => {
  const { fromDate, toDate } = payload;
  let response = {};
  const url = `/admin/metric/licenses/all?fromDate=${fromDate || ''}&toDate=${toDate || ''}`;
  const cacheID = `${fromDate}_${toDate}`;

  try {
    response = await axiosInstanceCached.get(url, {
      id: `adminLicenses-${cacheID}`,
      cache: {
        ttl: 1000 * 60 * 5, // 5 minutes
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get user licenses
export const userLicenses = async ({ email = '', userID = '', isUUID = false }) => {
  let response = {};
  const data = email || userID;

  try {
    response = await axiosInstance.get(
      `/admin/licenses?${isUUID ? 'userID=' : 'email='}${encodeURIComponent(data)}`
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get user licenses with partial search
export const userLicensesPartial = async ({ email = '', userID = '', licenseID = '' }) => {
  let response = {};
  const data = email || userID || licenseID;

  const url = `/admin/licenses/search?${
    userID ? 'userID=' : licenseID ? 'licenseID=' : 'email='
  }${encodeURIComponent(data)}`;

  try {
    response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Deactivate product on user license
// @ {
// @   "licenseID": "ID",
// @   "transactionID": "txnp_39cb3f78fdd1ba33"
// @    "deactivatedAt":"2022-05-06T13:58:53.911091-04:00" OPTIONAL
// @ }
export const deactivateLicenseProduct = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.patch(`/admin/licenses/transactions`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get daily sales log
export const dailySalesLog = async (payload = {}) => {
  let response = {};
  await deleteDataCache(axiosInstanceCached, payload?.deleteCache, 'dailySalesLog');
  const { fromDate } = payload;
  const url = `/admin/webstore/saleorder/log${
    fromDate ? `?fromDate=${encodeURIComponent(fromDate)}` : ''
  }`;

  try {
    response = await axiosInstanceCached.get(url, {
      id: `dailySalesLog`,
      cache: {
        ttl: 1000 * 60 * 15, // 15 min
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Export sales logs to CSV by date range
export const salesLogExport = async (payload = {}) => {
  const { fromDate, toDate } = payload;
  let response = {};

  try {
    //* export=json returns different data
    // response = await axiosInstance.get(
    //   `/webstore/saleorder?export=json&fromDate=${fromDate || ''}&toDate=${toDate || ''}`
    // );

    response = await axiosInstance.get(
      `/admin/webstore/saleorder?export=csv&fromDate=${fromDate || ''}&toDate=${toDate || ''}`
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Redeem product to license
// @ "skucode":"",
// @ "userID": "", OR
// @ "email": "",
// @ "licenseID": "",
// @ "quantity": 1,
// @ "licenseType": "desktop" OR "mobile"
// @ "createLicense": 'default' OR 'multi_sport_core' OR null
export const redeemProduct = async (payload = {}) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/licenses/skus`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Deactivate user license
// @ {
// @   "licenseID": "ID",
// @ }
export const deactivateLicense = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.patch(`/admin/licenses/disable`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Re-activate user license
// @ {
// @   "licenseID": "ID",
// @ }
export const activateLicense = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.patch(`/admin/licenses/enable`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Add new license
// @ {
// @   "userID": "ID",
// @   "licenseType": "desktop or mobile",
// @   "licenseKey": "desktop_license or mobile_license"
// @ }
export const createLicense = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/licenses`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Update a user license
// @ {
// @   "license_id": "ID",
// @   "expires_at": "2026-08-15T23:59:59-06:00" OR null to clear
// @ }
export const licenseUpdate = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/license_update`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get list of tracking systems - OLD NON-ADMIN SETUP
export const listTrackingSystems = async () => {
  let response = {};

  try {
    response = await axiosInstanceCached.get(`/trackingsystems`, {
      id: `trackingSystems`,
      cache: {
        ttl: 1000 * 60 * 60 * 4, // 4 hours
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get store report
export const storeReport = async () => {
  let response = {};

  try {
    response = await axiosInstance.get(`/admin/webstore/report`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get store report filter
export const storeReportFilter = async (reportID = '') => {
  let response = {};

  try {
    response = await axiosInstance.get(`/admin/webstore/reportfilter?reportID=${reportID}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Set store report list
// @ "report_id":"",
// @ "name": "skucode",
// @ "values": ['SKUCODE1', 'SKUCODE2'],
export const setStoreReportFilter = async (payload = []) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/webstore/reportfilter/set`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get License redemptions
export const licenseRedemption = async ({ fromDate = '', toDate = '' }) => {
  let response = {};

  try {
    response = await axiosInstance.get(
      `/admin/webstore/licenseredemption?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Get expiring subscriptions
export const expiringSubscriptions = async ({ fromDate = '', toDate = '' }) => {
  let response = {};

  try {
    response = await axiosInstance.get(
      `/admin/webstore/expiringsubscription?fromDate=${fromDate}&toDate=${toDate}`
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Set store report list
// @ user param enter user email or uuid (user=),
// @ toDate param sets the ending date to display transactions, default is last 120 days
// @ fromDate param sets the starting date to display transactions, default is last 120 days
// @ type param: enter either software or physical to get specific transaction type (type=)
// @ default returns all software? transactions
export const transactions = async (payload = {}) => {
  let response = {};
  const { user, type, fromDate, toDate } = payload;

  const url = `/admin/transactions?type=${type}${fromDate ? `&fromDate=${fromDate}` : ''}${
    toDate ? `&toDate=${toDate}` : ''
  }${user ? `&user=${user}` : ''}`;

  try {
    response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// //* clubhouseGameModeStats return club events stats
// export const clubhouseGameModeStats = async ({ clubID, fromDate = '', toDate = '' }) => {
//   let response = {};

//   try {
//     response = await axiosInstance.get(
//       `/clubhouse/${clubID}/metrics/games?fromDate=${fromDate}&toDate=${toDate}`
//     );
//     return response;
//   } catch (error) {
//     return handleApiError(error);
//   }
// };

//* Get list of product keys
// export const productKeys = async (payload) => {
//   let response = {};
//   await deleteDataCache(axiosInstanceCached, payload?.deleteCache, 'adminProductKeys');

//   try {
//     response = await axiosInstanceCached.get(`/admin/productkeys`, {
//       id: `adminProductKeys`,
//       cache: {
//         ttl: 1000 * 60 * 60 * 4, // 4 hours
//       },
//     });
//     return response;
//   } catch (error) {
//     return handleApiError(error);
//   }
// };

//* Admin - Get list of product keys - uses limit and offset for pagination setup
// @ limit = number of keys per page to display
// @ offset = used to offset number of items based on limit per page to display
// @ createdFromDate = used to search keys from this createdAt date to future createdAt date
// @ createdToDate = used to search keys from past createdAt date to this createdAt date
// @ redeemedFromDate = used to search keys from this updatedAt date to future updatedAt date
// @ redeemedToDate = used to search keys from past updatedAt date to this updatedAt date
export const productKeys = async ({
  limit = '',
  offset = '',
  batchID = '',
  createdByID = '',
  productKey = '',
  skucode = '',
  skucodes = '',
  userID = '',
  userEmail = '',
  type = '',
  partnerID = '',
  desktopLicenseID = '',
  mobileLicenseID = '',
  redeemedFromDate = '',
  redeemedToDate = '',
  createdFromDate = '',
  createdToDate = '',
}) => {
  let response = {};
  let url = `/admin/productkeys?limit=${limit}&offset=${offset}`;

  // @ FILTER SETUP ===========================================
  //* Batch UUID filter
  if (batchID) {
    url = `${url}&batch_uuid=${batchID}`;
  }
  //* Created By ID filter
  if (createdByID) {
    url = `${url}&created_by_user_id=${createdByID}`;
  }
  //* Product Key filter
  if (productKey) {
    url = `${url}&product_key=${productKey}`;
  }
  //* User ID filter
  if (userID) {
    url = `${url}&user_id=${userID}`;
  }
  //* User Email filter
  if (userEmail) {
    url = `${url}&email=${userEmail}`;
  }
  //* Sku code(s) filter
  if (skucode) {
    url = `${url}&skucode=${skucode}`;
  }
  if (skucodes) {
    skucodes.forEach((sku) => {
      url = `${url}&skucodes=${sku}`;
    });
  }
  //* Key Type
  if (type) {
    url = `${url}&type=${type}`;
  }
  //* Partner ID
  if (partnerID) {
    url = `${url}&partner_id=${partnerID}`;
  }
  //* Desktop License ID filter
  if (desktopLicenseID) {
    url = `${url}&desktop_license_id=${desktopLicenseID}`;
  }
  //* Mobile License ID filter
  if (mobileLicenseID) {
    url = `${url}&mobile_license_id=${mobileLicenseID}`;
  }
  //* Updated At/Redeemed At from and to dates
  if (redeemedFromDate) {
    url = `${url}&updated_at_gteq=${redeemedFromDate}`;
  }
  if (redeemedToDate) {
    url = `${url}&updated_at_lteq=${redeemedToDate}`;
  }
  //* Created At from and to dates
  if (createdFromDate) {
    url = `${url}&created_at_gteq=${createdFromDate}`;
  }
  if (createdToDate) {
    url = `${url}&created_at_lteq=${createdToDate}`;
  }

  try {
    response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Create product keys
// @ Payload should contain
// @ Array of objects
// @ Each Object should follow this structure
// @ {
// @   "productKey": "CLTS1-7A9GP-GJYYF-6BBTY", Generated key and passed in
// @   "skucodes": ["JKSKU01", "PEBBCP"], OR just following if single sku
// @   "skucode": "PEBBCP",
// @   "type": "desktop" or "mobile",
// @   "createdBy": UUID of admin user OPTIONAL but should include
// @ },
export const createProductKeys = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/productkeys`, payload, { timeout: 90000 });
    await deleteDataCache(axiosInstanceCached, true, 'adminProductKeys');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Delete a single product key
export const productKeyDelete = async (key) => {
  let response = {};

  try {
    response = await axiosInstance.delete(`/admin/productkeys/${key}`);
    await deleteDataCache(axiosInstanceCached, true, 'adminProductKeys');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Set status for a single product key
export const productKeyStatus = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/productkeys/status`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'adminProductKeys');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// @ New Admin endpoints =================================
//* Admin - Game Modes
export const gameModes = async (payload) => {
  let response = {};
  await deleteDataCache(axiosInstanceCached, payload?.deleteCache, 'adminGameModes');

  try {
    response = await axiosInstanceCached.get(`/admin/game_modes`, {
      id: `adminGameModes`,
      cache: {
        ttl: 1000 * 60 * 60 * 4, // 4 hours
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Game Mode Details
export const gameMode = async (key) => {
  let response = {};

  try {
    response = await axiosInstance.get(`/admin/game_modes/${key}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Edit Game Mode Details
export const gameModeEdit = async (payload) => {
  let response = {};
  const { currentKey } = payload;

  try {
    response = await axiosInstance.put(`/admin/game_modes/${currentKey}`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'adminGameModes');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Delete Game Mode
export const gameModeDelete = async (payload) => {
  let response = {};
  const { key = '' } = payload;

  try {
    response = await axiosInstance.delete(`/admin/game_modes/${key}`);
    await deleteDataCache(axiosInstanceCached, true, 'adminGameModes');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Create Game Mode
// @ Payload should contain
// @ {
// @   "key": "test_key"
// @   "name": "Test key"
// @ },
export const gameModeCreate = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/game_modes`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'adminGameModes');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Simulator Permissions
export const simulatorPermissions = async (payload) => {
  let response = {};
  await deleteDataCache(axiosInstanceCached, payload?.deleteCache, 'adminSimPermissions');

  try {
    response = await axiosInstanceCached.get(`/admin/simulator_permissions`, {
      id: `adminSimPermissions`,
      cache: {
        ttl: 1000 * 60 * 60 * 4, // 4 hours
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Simulator Permission Details
export const simulatorPermission = async (key) => {
  let response = {};

  try {
    response = await axiosInstance.get(`/admin/simulator_permissions/${key}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Delete Simulator Permission
export const simulatorPermissionDelete = async (payload) => {
  let response = {};
  const { key = '' } = payload;

  try {
    response = await axiosInstance.delete(`/admin/simulator_permissions/${key}`);
    await deleteDataCache(axiosInstanceCached, true, 'adminSimPermissions');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Edit Simulator Permission Details
export const simulatorPermissionEdit = async (payload) => {
  let response = {};
  const { currentKey } = payload;

  try {
    response = await axiosInstance.put(`/admin/simulator_permissions/${currentKey}`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'adminSimPermissions');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Create Simulator Permission
// @ Payload should contain
// @ {
// @   "key": "test_key"
// @   "name": "Test key"
// @ },
export const simulatorPermissionCreate = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/simulator_permissions`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'adminSimPermissions');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Tracking Systems
export const trackingSystems = async (payload) => {
  let response = {};
  await deleteDataCache(axiosInstanceCached, payload?.deleteCache, 'adminTrackingSystems');

  try {
    response = await axiosInstanceCached.get(`/admin/tracking_systems`, {
      id: `adminTrackingSystems`,
      cache: {
        ttl: 1000 * 60 * 60 * 4, // 4 hours
      },
    });

    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Tracking System Details
export const trackingSystem = async (key) => {
  let response = {};

  try {
    response = await axiosInstance.get(`/admin/tracking_systems/${key}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Delete Simulator Permission
export const trackingSystemDelete = async (payload) => {
  let response = {};
  const { key = '' } = payload;

  try {
    response = await axiosInstance.delete(`/admin/tracking_systems/${key}`);
    await deleteDataCache(axiosInstanceCached, true, 'adminTrackingSystems');
    await deleteDataCache(axiosInstanceCached, true, 'trackingSystems');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Edit Simulator Permission Details
export const trackingSystemEdit = async (payload) => {
  let response = {};
  const { currentKey } = payload;

  try {
    response = await axiosInstance.put(`/admin/tracking_systems/${currentKey}`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'adminTrackingSystems');
    await deleteDataCache(axiosInstanceCached, true, 'trackingSystems');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Create Tracking System
// @ Payload should contain
// @ {
// @   "key": "test_key"
// @   "name": "Test name"
// @   "description": "Test description"
// @   "imageURL": "Test URL"
// @   "manufacturer": "Test manufacturer name"
// @   "webSiteURL": "Test site URL"
// @   "supportedPlatforms": ["windows", "ios"]
// @   "public": true/false
// @   "engineIntegrated": true/false
// @ },
export const trackingSystemCreate = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/tracking_systems`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'adminTrackingSystems');
    await deleteDataCache(axiosInstanceCached, true, 'trackingSystems');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Bundles
export const bundles = async (payload) => {
  let response = {};
  await deleteDataCache(axiosInstanceCached, payload?.deleteCache, 'adminBundles');

  try {
    response = await axiosInstanceCached.get(`/admin/bundles`, {
      id: `adminBundles`,
      cache: {
        ttl: 1000 * 60 * 60 * 4, // 4 hours
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Bundle Details
export const bundle = async ({ id, deleteCache = false }) => {
  let response = {};
  await deleteDataCache(axiosInstanceCached, deleteCache, `adminBundle-${id}`);

  try {
    response = await axiosInstance.get(`/admin/bundles/${id}`);
    // // response = await axiosInstanceCached.get(`/admin/bundles/${id}`, {
    //   id: `adminBundle-${id}`,
    //   cache: {
    //     ttl: 1000 * 60 * 60 * 4, // 4 hours
    //   },
    // });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Create New Bundle
export const bundleCreate = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/bundles`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'adminBundles');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Edit Bundle Details
export const bundleEdit = async (payload) => {
  let response = {};
  const { id } = payload;

  try {
    response = await axiosInstance.put(`/admin/bundles/${id}`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'adminBundles');
    await deleteDataCache(axiosInstanceCached, true, `adminBundle-${id}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Delete Bundle
export const bundleDelete = async (payload) => {
  let response = {};
  const { id = '' } = payload;

  try {
    response = await axiosInstance.delete(`/admin/bundles/${id}`);
    await deleteDataCache(axiosInstanceCached, true, 'adminBundles');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Courses
// @ construction_type: igolf, built, all
export const courses = async (payload) => {
  let response = {};
  await deleteDataCache(axiosInstanceCached, payload?.deleteCache, 'adminCourses');

  try {
    response = await axiosInstanceCached.get(
      `/admin/courses?construction_type=${payload?.construction_type}`,
      {
        id: `adminCourses_${payload?.construction_type}`,
        cache: {
          ttl: 1000 * 60 * 60 * 4, // 4 hours
        },
      }
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Course paginated list
// @ limit = number of courses per page to display
// @ offset = used to offset number of items based on limit per page to display
// @ construction_type: igolf, built, all (defaults to all)
// @ search = optional search term
// @ game: apex, connect (optional)
// * response: { data: Course[], total_count: number }
export const coursesPaginated = async ({
  limit = '',
  offset = '',
  construction_type = 'all',
  search = '',
  game = '',
} = {}) => {
  let response = {};
  const params = new URLSearchParams();
  if (limit !== '' && limit !== null && limit !== undefined) params.set('limit', limit);
  if (offset !== '' && offset !== null && offset !== undefined) params.set('offset', offset);
  if (construction_type) params.set('construction_type', construction_type);
  if (search) params.set('search', search);
  if (game) params.set('game', game);

  const url = `/admin/courses?${params.toString()}`;

  try {
    response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Course - get a single course details
export const course = async (payload) => {
  const { id } = payload;
  let response = {};
  await deleteDataCache(axiosInstanceCached, payload?.deleteCache, `adminCourse_${id}`);

  try {
    response = await axiosInstanceCached.get(`/admin/courses/${id}`, {
      id: `adminCourse_${id}`,
      cache: {
        ttl: 1000 * 60 * 60 * 4, // 4 hours
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - editCourse - edit a single course details
export const editCourse = async (payload) => {
  const { id } = payload;
  let response = {};

  try {
    response = await axiosInstance.put(`/admin/courses/${id}`, payload);
    await deleteDataCache(axiosInstanceCached, true, `adminCourse_${id}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Delete Course Version
export const courseDelete = async (payload) => {
  // console.log('course delete api payload:', payload);
  let response = {};
  const { courseID } = payload;

  try {
    response = await axiosInstance.delete(`/admin/courses/${courseID}`);
    await deleteDataCache(axiosInstanceCached, true, `adminCourse_${courseID}`);
    await deleteDataCache(axiosInstanceCached, true, 'adminCourses');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Push Course To Prod
export const courseProdSync = async (payload) => {
  let response = {};
  const { id } = payload;

  try {
    response = await axiosInstance.post(`/admin/course_sync`, payload);
    await deleteDataCache(axiosInstanceCached, true, `adminCourse_${id}`);
    await deleteDataCache(axiosInstanceCached, true, 'adminCourses');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - courseVersion - get course version details
export const courseVersion = async (payload) => {
  const { versionID } = payload;
  let response = {};
  await deleteDataCache(
    axiosInstanceCached,
    payload?.deleteCache,
    `adminCourseVersion_${versionID}`
  );

  try {
    response = await axiosInstanceCached.get(`/admin/course_versions/${versionID}`, {
      id: `adminCourseVersion_${versionID}`,
      cache: {
        ttl: 1000 * 60 * 60 * 4, // 4 hours
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Edit Course Version
export const courseVersionEdit = async (payload) => {
  let response = {};
  const { versionID, courseID } = payload;

  try {
    response = await axiosInstance.put(`/admin/course_versions/${versionID}`, payload);
    await deleteDataCache(axiosInstanceCached, true, `adminCourseVersion_${versionID}`);
    await deleteDataCache(axiosInstanceCached, true, `adminCourse_${courseID}`);
    await deleteDataCache(axiosInstanceCached, true, 'adminCourses');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Delete Course Version
export const courseVersionDelete = async (payload) => {
  let response = {};
  const { versionID, courseID } = payload;

  try {
    response = await axiosInstance.delete(`/admin/course_versions/${versionID}`);
    await deleteDataCache(axiosInstanceCached, true, `adminCourseVersion_${versionID}`);
    await deleteDataCache(axiosInstanceCached, true, `adminCourse_${courseID}`);
    await deleteDataCache(axiosInstanceCached, true, 'adminCourses');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* ADMIN - Get list of products
export const products = async ({ deleteCache = false }) => {
  let response = {};
  await deleteDataCache(axiosInstanceCached, deleteCache, 'adminProducts');

  try {
    response = await axiosInstanceCached.get(`/admin/products`, {
      id: `adminProducts`,
      cache: {
        ttl: 1000 * 60 * 60 * 4, // 4 hours
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* ADMIN - Get product details
export const product = async (sku) => {
  let response = {};

  try {
    response = await axiosInstance.get(`/admin/products/${sku}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Edit Product Details
export const productEdit = async (payload) => {
  let response = {};
  const { product_id: id } = payload;

  try {
    response = await axiosInstance.put(`/admin/products/${id}`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'adminProducts');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Create New Product
export const productCreate = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/products`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'adminProducts');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// TODO Switch to admin route
export const showProduct = async (id) => {
  let response = {};

  try {
    response = await axiosInstance.get(`/webstore/products/${id}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

// TODO Switch to admin route
export const showSkuProduct = async (sku) => {
  let response = {};

  try {
    response = await axiosInstance.get(`/webstore/products/sku/${sku}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Create Clubmember
export const createClubmember = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/clubmembers`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - User Search - returns uuid, displayName, email
export const userSearch = async (payload) => {
  let response = {};
  const { q } = payload;

  try {
    response = await axiosInstance.get(`/admin/users/search?q=${encodeURIComponent(q)}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Get list of users - uses limit and offset for pagination setup
// @ q = user search param
// @ p = user search param for permission search
// @ limit = number of users per page to display
// @ offset = used to offset number of items based on limit per page to display
// @ fromDate = used to search users from this createdAt date to future createdAt date
// @ toDate = used to search users from past createdAt date to this createdAt date
export const users = async ({ limit = '', offset = '', q = '', p = '' }) => {
  let response = {};
  const url = `/admin/users?q=${encodeURIComponent(q)}&p=${p}&limit=${limit}&offset=${offset}`;

  try {
    response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Get user details
// @ id = user ID to retrieve
export const user = async ({ id = '' }) => {
  let response = {};
  const url = `/admin/users/${id}`;

  try {
    response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Edit User Details
export const userEdit = async (payload) => {
  let response = {};
  const { id } = payload;

  try {
    response = await axiosInstance.put(`/admin/users/${id}`, payload);
    await deleteDataCache(axiosInstanceCached, true, `user_show_${id}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Ghost User
export const ghostUser = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/users/${payload.userID}/ghost`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Activate User
export const activateByID = async (userID) => {
  let response = {};
  try {
    response = await axiosInstance.post(`/admin/users/${userID}/activate`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - GET Partner List
export const partners = async () => {
  let response = {};

  try {
    response = await axiosInstanceCached.get(`/admin/partners`, {
      id: `adminPartners`,
      cache: {
        ttl: 1000 * 60 * 60 * 4, // 4 hours
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - GET Partner
export const partner = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.get(`/admin/partners/${payload.id}`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Create Partner
export const createPartner = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/partners`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Update Partner
export const updatePartner = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.put(`/admin/partners`, payload);
    await deleteDataCache(axiosInstanceCached, true, `adminPartners`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Partner - GET Partner Product Key Report
export const partnerProductKeyReport = async ({ fromDate, toDate, partner }) => {
  let response = {};
  let url = `/admin/productkeys/report?fromDate=${fromDate}&toDate=${toDate}`;

  // @ FILTER SETUP ===========================================
  //* Partner ID
  if (partner) {
    url = `${url}&partner=${partner}`;
  }

  try {
    response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Game Versions
export const gameVersions = async (payload) => {
  let response = {};
  await deleteDataCache(axiosInstanceCached, payload?.deleteCache, 'adminGameVersions');

  try {
    response = await axiosInstanceCached.get(`/admin/game_versions`, {
      id: `adminGameVersions`,
      cache: {
        ttl: 1000 * 60 * 60 * 4, // 4 hours
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Edit Game Version Details
export const gameVersionUpdate = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.put(`/admin/game_versions`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'adminGameVersions');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Create Game Version
// @ Payload should contain
// @ {
// @   "os": "windows"
// @   "version": "1.0.2.4"
// @ },
export const gameVersionCreate = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/game_versions`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'adminGameVersions');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Delete a single downloadVersion
export const downloadDelete = async (payload) => {
  let response = {};
  const { id } = payload;

  try {
    response = await axiosInstance.delete(`/admin/download_versions/${id}`);
    // await deleteDataCache(axiosInstanceCached, true, 'adminProductKeys');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Post download log
// @ Payload should contain
// @ {
// @   "download_version_id": "2ea1474b-fd04-432a-a62e-1eabcf7f9811"
// @ },
export const downloadLog = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/downloads`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Create a new clubhouse
export const createClubhouse = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/clubhouse/create`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'clubhouse_list');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* delete a clubhouse
export const deleteClubhouse = async (uuid) => {
  let response = {};

  try {
    response = await axiosInstance.delete(`/admin/clubhouse/${uuid}`);
    await deleteDataCache(axiosInstanceCached, true, 'clubhouse_list');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin requests log
export const adminRequests = async () => {
  let response = {};

  try {
    response = await axiosInstance.get(`/admin/admin_requests`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Product Boards =====================================
export const boardList = async () => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/board_list`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const boardGet = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/board_get`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const boardCreate = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/board_create`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const boardUpdate = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/board_update`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

export const boardDelete = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/board_delete`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Get list of events - uses limit and offset for pagination setup
// @ paginated, with "page_info" object and "events" array
// @ limit = number of events per page to display
// @ offset = used to offset number of items based on limit per page to display
// @ starts_at_gte = used to search events from this date to future date
// @ ends_at_lte = used to search events from past date to this date
// @ Example payload
// @ {
// @   "page_info": {
// @       "limit": 5,
// @       "offset": 5
// @   },
// @   "id": "2fa515eg-5483-6984-b69c-26f4a9346b4c",
// @   "name": "KP",
// @   "game_mode": "closest",
// @   "clubhouse_name": "test",
// @   "starts_at_gte": "2019-07-26",
// @   "ends_at_lte": "2019-08-31"
// @ }
export const eventList = async (payload = {}) => {
  let response = {};
  // const url = `/admin/event_list?q=${encodeURIComponent(q)}&p=${p}&limit=${limit}&offset=${offset}`;

  try {
    response = await axiosInstance.post(`/admin/event_list`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Get Single Event details
// @ requires 'id' in body
export const eventGet = async (payload = {}) => {
  let response = {};
  const url = `/admin/event_get`;

  try {
    response = await axiosInstance.post(url, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Edit Event Details
// @ only update fields sent in
export const eventEdit = async (payload = {}) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/event_update`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Event Delete
// @ requires 'id' in body, returns 204 if successfull (no content)
export const eventDelete = async (payload = {}) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/event_delete`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Event Create - not set up for admins to create events
export const eventCreate = async (payload = {}) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/event_create`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Challenges
export const challenges = async (payload = {}) => {
  let response = {};
  await deleteDataCache(axiosInstanceCached, payload?.deleteCache, 'adminChallenges');

  try {
    response = await axiosInstanceCached.post(`/admin/challenge_list`, payload, {
      id: 'adminChallenges',
      cache: {
        ttl: 1000 * 60 * 60 * 4, // 4 hours
      },
    });
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Get Single Challenge details
// @ requires 'id' in body
export const challengeGet = async (payload = {}) => {
  let response = {};
  const url = `/admin/challenge_get`;

  try {
    response = await axiosInstance.post(url, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Edit Challenge Details
export const challengeEdit = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/challenge_update`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'adminChallenges');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Create Challenge
// @ Payload should contain
// @ {
// @   "id": "Testaroni",
// @   "name": "Testaroni and cheese",
// @   "short_description": "testaroni short description",
// @   "long_description": "testaroni long description",
// @   "category": "scrambling",
// @   "shots_allowed": 5,
// @   "status": "disabled",
// @   "display_order": 25
// @ }
export const challengeCreate = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/challenge_create`, payload);
    await deleteDataCache(axiosInstanceCached, true, 'adminChallenges');
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* ping
export const ping = async (payload = {}) => {
  let response = {};
  const url = `/ping`;

  try {
    response = await axiosInstance.get(url);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - TruSim API partners
export const apiPartnerList = async (payload = {}) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/trusimapi_partner_list`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Get Single api partner details
// @ requires 'id' in body
export const apiPartnerGet = async (payload = {}) => {
  let response = {};
  const url = `/admin/trusimapi_partner_get`;

  try {
    response = await axiosInstance.post(url, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Update Single api partner details
// @ requires 'id' in body
export const apiPartnerUpdate = async (body) => {
  const url = `/admin/trusimapi_partner_update`;

  let response = {};
  const data = new FormData();
  // ! Clearing an image is signalled by a null *_url inside this JSON payload, not by
  // ! omitting the file part — FormData values are always coerced to strings or Blobs.
  data.append('data', JSON.stringify(body.payload));
  if (body.logo) {
    data.append('image', body.logo);
  }

  if (body.deviceImage) {
    data.append('new_image', body.deviceImage);
  }

  const config = {
    method: 'post',
    maxBodyLength: Infinity,
    url,
    data,
  };

  try {
    response = await axiosInstance.request(config);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Sync Single api partner to production
// @ requires 'id' in body which is the trusimapi_partner_id
export const apiPartnerSync = async (payload) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/trusimapi_partner_sync`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Export quarterly report to CSV by date range
export const quarterlyReportExport = async (payload = {}) => {
  const { fromDate, toDate } = payload;
  let response = {};

  try {
    //* export=json returns different data
    // response = await axiosInstance.get(
    //   `/webstore/saleorder?export=json&fromDate=${fromDate || ''}&toDate=${toDate || ''}`
    // );

    response = await axiosInstance.get(
      `/admin/sales_report?export=csv&fromDate=${fromDate || ''}&toDate=${toDate || ''}`
    );
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Post API docs
export const apiDocs = async () => {
  let response = {};

  try {
    response = await axiosInstance.get(`/admin/docs`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Get list of scheduler APIs
// @ response:
// @ [
// @     {
// @         "scheduler_id": "test",
// @         "license_id": "6c885f0a-7f52-41c4-9f16-ae1e3d72eb22",
// @         "license_binding_status": "active",
// @         "user_id": "2a5c8c75-c02c-4693-9b93-3ce0033863b6",
// @         "user_email": "test@example.com"
// @     },
// @     ...
// @ ]
export const schedulerAPIList = async () => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/scheduler_license_list`);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Get details for a specific scheduler API
// @ payload:
// @ {
// @     "scheduler_id": "test",
// @     "license_id": "6c885f0a-7f52-41c4-9f16-ae1e3d72eb22",
// @ }
// @ response:
// @ {
// @     "scheduler_id": "test",
// @     "license_id": "6c885f0a-7f52-41c4-9f16-ae1e3d72eb22",
// @     "license_binding_status": "active",
// @     "user_id": "2a5c8c75-c02c-4693-9b93-3ce0033863b6",
// @     "user_email": "test@example.com",
// @     "scheduler_messages": [
// @         {
// @             "id": "c731481c-886b-459e-97df-c787edfa5450",
// @             "scheduler_id": "test",
// @             "license_id": "6c885f0a-7f52-41c4-9f16-ae1e3d72eb22",
// @             "command": "on",
// @             "message": "Your session is starting now.",
// @             "created_at": "2026-04-21T22:40:29.565705Z",
// @             "sent_at": "2026-04-21T22:40:29.61779Z"
// @         },
// @         ...
// @     ]
// @ }
export const schedulerAPIGet = async (payload = {}) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/scheduler_license_get`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};

//* Admin - Send message to a specific scheduler API
// @ off: locks the game client, displays message that session has ended and has optional "url" param that will be a QR code displayed
// @ on: unlocks the game client, boots player to lobby, clears roster
// @ continue: unlocks the game client, continues as you were
// @ warning: popup in the game client with a "done" button that dismisses the popup, displays "session will end soon" and has optional "url" param that will be a QR code

// @ payload:
// @ {
// @     "scheduler_id": "test",
// @     "license_id": "6c885f0a-7f52-41c4-9f16-ae1e3d72eb22",
// @     "command": "off",
// @     "message": "Optional custom copy for off / warning",
// @     "url": "https://example.com/qr-code"
// @ }
// @ response:
// @ {
// @     "id": "85594160-6055-4e83-a364-89d682409a2f",
// @     "scheduler_id": "test",
// @     "license_id": "7c995f0a-7f58-41c4-9e16-ae1e3d72eb33",
// @     "command": "on",
// @     "message": "Your session is starting now. Scan to extend or manage bookings.",
// @     "url": "test",
// @     "created_at": "2026-04-22T11:19:58.196347-06:00"
// @ }
export const schedulerMessageSend = async (payload = {}) => {
  let response = {};

  try {
    response = await axiosInstance.post(`/admin/scheduler_message`, payload);
    return response;
  } catch (error) {
    return handleApiError(error);
  }
};
